import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { GuardrailsConfig } from '@/types';
import { GuardrailsItem } from './guardrails-item';
import { Prompt } from './prompt';
import { getGuardrailsConfigs } from '@/services/guardrails/get-guardrails-configs.ts';
import { GuardrailsSkeleton } from './guardrails-skeleton';
import { Separator } from '@/components/ui/separator';
import { useNotification } from '@/components/notifications';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { Play } from 'lucide-react';
import { LoadingSpinner } from '@/components/loading-spinner';

const emptyConfig: GuardrailsConfig = {
  guardrail_id: '',
  active: false,
  name: '',
  guardrail_description: '',
  examples: [
    {
      result: 'Pass',
      user_query: '',
    },
  ],
  fail_criteria: '',
  pass_criteria: '',
  override: false,
};

export const GuardrailsPage = () => {
  const notification = useNotification();
  const [configsList, setConfigsList] = useState<GuardrailsConfig[]>([]);
  const [isAddNewConfigState, setIsAddNewConfigState] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);

  const hasGuardrailsEnabled = useMemo(
    () => configsList.some((item) => item.active),
    [configsList],
  );

  useEffect(() => {
    const fetchConfigs = async () => {
      setLoading(true);
      try {
        const result = await getGuardrailsConfigs();
        if (result?.detail) {
          notification.error(result.detail);
          setConfigsList([]);
          return;
        }
        setConfigsList(result.data as GuardrailsConfig[]);
      } catch (error) {
        console.error('Get guardrails configs error:', error);
        notification.error('An unexpected error occurred');
        setConfigsList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, [notification]);

  const addNewConfig = (config: GuardrailsConfig) => {
    // TODO: remove this after backend will be updated
    // currently BE sends true or false as string
    const newConfig = {
      ...config,
      override: false,
      active: false,
    };

    setConfigsList([...configsList, newConfig]);
    setIsAddNewConfigState(false);
  };

  const deleteConfig = (id: string) => {
    const updatedConfigs = configsList.filter(
      (item) => item.guardrail_id !== id,
    );
    setConfigsList(updatedConfigs);
  };

  const handleUpdateConfig = (config: GuardrailsConfig) => {
    const updatedConfigs = configsList.map((item: GuardrailsConfig) => {
      if (item.guardrail_id === config.guardrail_id) {
        return config;
      }

      return item;
    });

    setConfigsList(updatedConfigs);
  };

  const handleCancelCreatingNewConfig = () => {
    setIsAddNewConfigState(false);
  };

  return (
    <div className='w-full'>
      <div className='mb-6'>
        <PageTitle title='Guardrails configs' />
        <PageDescription>
          Guardrails help ensure your content stays consistent, reliable, and
          aligned with your standards. This interface is for building and
          testing Guardrails. When Guardrails are enabled here, your test prompt
          runs against the active Guardrails listed below. In Composer,
          Guardrails are enabled or disabled in the guardrail node itself.
        </PageDescription>
      </div>

      <Prompt disabled={!hasGuardrailsEnabled} />

      <Separator className='my-10' />
      <div className='relative'>
        {showOverlay && (
          <div className='fixed inset-0 flex items-center justify-center z-10 bg-background/50'>
            <LoadingSpinner />
          </div>
        )}
        <div className='flex justify-between items-center mb-4'>
          <h3 className='text-xl font-semibold'>Guardrails</h3>
          <div className='flex items-center gap-2'>
            <Button variant='outline' asChild>
              <a
                href='https://www.loom.com/share/6e144d1f5b944c97bd24983ba3be3f3e?sid=45d54f56-6736-45e4-830c-2cb88d8d39d9'
                target='_blank'
                rel='noopener noreferrer'
              >
                <Play />
                Help
              </a>
            </Button>
            <Button
              onClick={() => setIsAddNewConfigState(!isAddNewConfigState)}
            >
              New config
            </Button>
          </div>
        </div>

        <div className='overflow-hidden w-full flex flex-col gap-2'>
          {loading ? (
            <GuardrailsSkeleton />
          ) : (
            <>
              {isAddNewConfigState && (
                <GuardrailsItem
                  {...emptyConfig}
                  isEdit={true}
                  addNewConfig={addNewConfig}
                  handleCancelCreatingNewConfig={handleCancelCreatingNewConfig}
                  setShowOverlay={setShowOverlay}
                />
              )}
              {configsList.map((item: GuardrailsConfig) => (
                <GuardrailsItem
                  {...item}
                  key={item.guardrail_id}
                  handleUpdateConfig={handleUpdateConfig}
                  deleteConfig={deleteConfig}
                  setShowOverlay={setShowOverlay}
                />
              ))}
              {configsList.length === 0 && <div>No configs were found</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
